import styled from "styled-components";

const controlYourExperience = [
  "You can block users, delete comments, mute content, and choose which themes or folios you want to see from each user.",
  "You have full control of your algorithm and can tailor what appears in your feed.",
  "We draw clear lines: hate speech, especially racism, is not allowed.",
  "Passionate language may appear, but harassment or discriminatory attacks are never allowed.",
  "You set your boundaries and we protect the space so it stays honest and peaceful.",
];

const prohibitedContent = [
  {
    title: "A. Sexual & Explicit Content",
    items: [
      "Pornographic or sexually explicit visuals",
      "Nudity, sexual acts, or sexually suggestive imagery",
      "Gore, graphic injuries, or disturbing bloody scenes",
    ],
  },
  {
    title: "B. Violence, Terrorism & Threats",
    items: [
      "Real-world violent acts or glorification of violence",
      "Promotion or glorification of terrorism or extremist acts",
      "Threats of violence, harm, or targeted intimidation",
    ],
  },
  {
    title: "C. Self-Harm & Mental Health",
    items: [
      "Promotion or romanticization of self-harm or suicide",
      "Pro-eating disorder or dangerous behavior content",
      "Thoughtful, respectful storytelling is welcome",
    ],
  },
  {
    title: "D. Hate Speech & Discrimination",
    items: [
      "Racist content or racial slurs",
      "Attacks based on race, religion, gender, nationality, or disability",
      "Dehumanizing or degrading speech targeting identity",
    ],
  },
  {
    title: "E. Political Content",
    items: [
      "Besties is a politically neutral platform",
      "No content promoting political ideologies, campaigns, movements, or agendas",
      "Includes endorsements, propaganda, or media provoking political debate",
      "If politics is mentioned but is not the focal point of the video or post, it is acceptable (for example, background political imagery)",
      "Focus on yourself and your besties; this is your space to connect, share, and breathe",
    ],
  },
  {
    title: "F. Language & Harassment",
    items: [
      "Strong opinions and passionate language may appear, but repeated personal attacks or targeted insults toward a specific user are not allowed",
      "No harassment campaigns, coordinated bullying, or stalking",
      "No hate speech or discriminatory language based on race, gender, or religion",
    ],
  },
  {
    title: "G. Personal Information & Privacy",
    items: [
      "Do not share another user's personal info (email, phone, address, etc.) without consent",
      "Do not share real-time or historical location without permission",
      "Doxing (publishing private info to harass) is strictly banned",
      "Do not share private messages, photos, or videos without consent",
      "Impersonation is allowed only with clear permission from the person being impersonated (for example, a fan account)",
    ],
  },
];

const consequences = [
  "Content removal",
  "Temporary feature restrictions",
  "Suspension or permanent account deletion",
];

const TerminationReasons = [
  "Violating these Terms",
  "Engaging in behavior that threatens platform safety or integrity",
];

const RestrictionList = [
  "Modify, translate, reverse-engineer, decompile, or create derivative works of any part of the Besties platform or its services",
  "Use a domain name or web URL in your username or profile without written consent",
  "Circumvent or bypass any security features or access restrictions",
];

const Terms = () => {
  const effectiveDate = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const contactEmail =
    process.env.REACT_APP_SUPPORT_EMAIL || "oquetel@creationbesties.com";

  return (
    <PageWrapper>
      <Content>
        <Title>Terms of Service</Title>
        <EffectiveDate>Effective Date: {effectiveDate}</EffectiveDate>
        <Paragraph>
          Welcome to Besties, a platform by Creation Besties ("we," "our," or
          "us"). These Terms of Service ("Terms") govern your use of the Besties
          mobile application and related services (the "Service"). By creating
          an account or using Besties in any way, you agree to these Terms. If
          you do not agree, please do not use the Service.
        </Paragraph>

        <Section>
          <SectionTitle>Our Approach to Speech and Community</SectionTitle>
          <Paragraph>
            At Besties, we believe in freedom of expression with
            responsibility. We want you to be yourself while keeping this a
            place where people can connect without fear, hate, or unnecessary
            drama.
          </Paragraph>
          <Paragraph>
            This platform is also a politically free zone. We are here for
            creativity, friendships, and shared interests, not campaigns,
            debates, ideologies, or agendas.
          </Paragraph>
          <Paragraph>That means:</Paragraph>
          <List>
            {controlYourExperience.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </List>
        </Section>

        <Section>
          <SectionTitle>1. Eligibility</SectionTitle>
          <Paragraph>
            You must be at least 13 years old to use Besties. By using the
            Service, you confirm that you meet this requirement and have the
            legal capacity to enter into these Terms.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. Account Registration</SectionTitle>
          <Paragraph>
            To access certain features, you must create an account using your
            full name, email address, and phone number. You agree to provide
            accurate and current information and to keep your login credentials
            secure. You may log in via Google or Apple Sign-In for convenience.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>3. Content Guidelines and Acceptable Use</SectionTitle>
          <Paragraph>
            Besties allows you to create folios, which are curated folders where
            you can organize your posts or up to 10-minute videos by theme. You
            can customize what type of content you want to see from others by
            selecting the themed folios from users you follow. This adjusts your
            preferences for your feed.
          </Paragraph>
          <Paragraph>Prohibited content includes:</Paragraph>
          <List>
            {prohibitedContent.map((group) => (
              <li key={group.title}>
                <strong>{group.title}</strong>
                <SubList>
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </SubList>
              </li>
            ))}
          </List>
          <Paragraph>Consequences for violations may include:</Paragraph>
          <List>
            {consequences.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </List>
        </Section>

        <Section>
          <SectionTitle>4. Monitoring and Reporting</SectionTitle>
          <Paragraph>
            We monitor public content to maintain platform safety. Private
            messages and media are only reviewed if explicitly reported by a
            user. If you see something wrong, report it and our team will act
            swiftly.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Intellectual Property</SectionTitle>
          <Paragraph>
            You retain ownership of all content you create on Besties. By
            posting, you give us a non-exclusive, royalty-free license to use
            and share your content to operate the platform. All branding, logos,
            and platform features are the property of Creation Besties and
            cannot be reused without permission.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>6. Privacy, Cookies, and Data Usage</SectionTitle>
          <Paragraph>
            We collect data to personalize your experience and keep users at
            Besties safe. This includes name, email, phone number, device info,
            and engagement behavior. We also use cookies and similar
            technologies for login, analytics, and ad personalization.
          </Paragraph>
          <Paragraph>Cookies help us:</Paragraph>
          <List>
            <li>Remember preferences and keep you logged in</li>
            <li>Understand user behavior for platform improvements</li>
            <li>Show more relevant content and ads</li>
          </List>
          <Paragraph>
            You can manage cookie settings in your device or browser. Full
            details can be found in our <a href="/privacy">Privacy Policy</a>.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Termination</SectionTitle>
          <Paragraph>We may suspend or remove your account at any time for:</Paragraph>
          <List>
            {TerminationReasons.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </List>
        </Section>

        <Section>
          <SectionTitle>8. Third-Party Services</SectionTitle>
          <Paragraph>
            We integrate services like Google and Apple for login convenience
            and other integrations. Use of these services is governed by their
            own terms and privacy policies.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9. Changes to Terms</SectionTitle>
          <Paragraph>
            We may revise these Terms from time to time. We will notify you
            in-app or by email. Continued use means you accept any updates.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Restrictions on Use</SectionTitle>
          <Paragraph>You agree not to:</Paragraph>
          <List>
            {RestrictionList.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </List>
        </Section>

        <Section>
          <SectionTitle>11. Ads and Sponsored Content</SectionTitle>
          <Paragraph>
            Besties displays ads and may include sponsored content to support
            the platform. Ads may be tailored based on your activity or the
            folios you follow. Broader, general ads may appear in explore or
            search feeds. We do not sell your personal data to advertisers, and
            we are not responsible for third-party brands or sponsors.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>12. Governing Law</SectionTitle>
          <Paragraph>
            These Terms are governed by the laws of Quebec, Canada. Disputes
            will be handled in the courts located in that jurisdiction.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>13. Contact</SectionTitle>
          <Paragraph>
            Have questions or need support? Contact us at{" "}
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
          </Paragraph>
        </Section>

        <Paragraph>
          By using Besties, you agree that you have read, understood, and
          accepted these Terms of Service.
        </Paragraph>
      </Content>
    </PageWrapper>
  );
};

export default Terms;

const PageWrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f7fafc;
  padding: 40px 20px;
`;

const Content = styled.div`
  max-width: 960px;
  margin: 0 auto;
  background-color: #ffffff;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  color: #1a202c;
  line-height: 1.7;
`;

const Title = styled.h1`
  margin: 0 0 12px 0;
  font-size: 32px;
  color: #2d3748;
`;

const EffectiveDate = styled.p`
  margin: 0 0 20px 0;
  font-weight: 600;
  color: #4a5568;
`;

const Section = styled.section`
  margin: 28px 0;
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px 0;
  font-size: 22px;
  color: #2d3748;
`;

const Paragraph = styled.p`
  margin: 8px 0;
  color: #2d3748;
`;

const List = styled.ul`
  margin: 8px 0 12px 18px;
  padding: 0;
  li {
    margin-bottom: 6px;
  }
`;

const SubList = styled.ul`
  margin: 6px 0 10px 20px;
  padding: 0;
  li {
    margin-bottom: 6px;
  }
`;
